import { useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function Press() {
  const [downloadStatus, setDownloadStatus] = useState<string>('');

  const pressKit = {
    companyName: 'TeamPro Management',
    tagline: 'Revolutionary Sports Team Management Platform',
    founded: '2024',
    description: 'TeamPro Management is the all-in-one platform transforming how sports teams operate. From NFL to youth leagues, we provide comprehensive solutions for team management, equipment procurement, and performance analytics.',
    keyFeatures: [
      'Automated Equipment Ordering & Fulfillment',
      'Real-time Commission Tracking',
      'Multi-Sport Team Management',
      'Premium Equipment Marketplace',
      'Secure Payment Processing',
      'Order Analytics Dashboard'
    ],
    stats: [
      { label: 'Products Available', value: '45+' },
      { label: 'Sports Covered', value: '5+' },
      { label: 'Commission Rate', value: '15%' },
      { label: 'Automated Processing', value: '100%' }
    ]
  };

  const mediaAssets = [
    {
      type: 'Logo Package',
      description: 'High-resolution logos in PNG, SVG, and vector formats',
      size: '2.4 MB',
      icon: 'ri-image-line'
    },
    {
      type: 'Product Screenshots',
      description: 'Platform interface and feature screenshots',
      size: '8.7 MB',
      icon: 'ri-screenshot-line'
    },
    {
      type: 'Brand Guidelines',
      description: 'Colors, typography, and usage guidelines',
      size: '1.2 MB',
      icon: 'ri-palette-line'
    },
    {
      type: 'Fact Sheet',
      description: 'Company overview and key statistics',
      size: '156 KB',
      icon: 'ri-file-text-line'
    }
  ];

  const pressReleases = [
    {
      date: 'January 2025',
      title: 'TeamPro Management Launches Revolutionary Sports Team Platform',
      excerpt: 'New platform combines team management, equipment procurement, and analytics in one seamless solution for sports organizations.',
      link: '#'
    },
    {
      date: 'January 2025',
      title: 'Automated Equipment Ordering System Transforms Team Operations',
      excerpt: 'TeamPro introduces zero-touch fulfillment system with automatic commission tracking for sports equipment sales.',
      link: '#'
    }
  ];

  const handleDownload = (assetType: string) => {
    setDownloadStatus(`Preparing ${assetType} for download...`);
    setTimeout(() => {
      setDownloadStatus('');
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-teal-500 to-teal-600 py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center">
              <h1 className="text-5xl font-bold text-white mb-6">Press Kit</h1>
              <p className="text-xl text-teal-50 max-w-3xl mx-auto">
                Media resources, company information, and brand assets for journalists, bloggers, and content creators.
              </p>
            </div>
          </div>
        </section>

        {/* Company Overview */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">About TeamPro Management</h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  {pressKit.description}
                </p>
                <div className="bg-gray-50 rounded-xl p-6 mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Key Features</h3>
                  <ul className="space-y-3">
                    {pressKit.keyFeatures.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <i className="ri-check-line text-teal-500 text-xl mt-0.5 flex-shrink-0"></i>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div>
                <div className="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-8 border border-teal-100">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Quick Facts</h3>
                  <div className="grid grid-cols-2 gap-6">
                    {pressKit.stats.map((stat, idx) => (
                      <div key={idx} className="text-center">
                        <div className="text-3xl font-bold text-teal-500 mb-2">{stat.value}</div>
                        <div className="text-sm text-gray-600">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 pt-8 border-t border-teal-100">
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Company:</span>
                        <span className="font-semibold text-gray-900">{pressKit.companyName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Founded:</span>
                        <span className="font-semibold text-gray-900">{pressKit.founded}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Industry:</span>
                        <span className="font-semibold text-gray-900">Sports Technology</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Media Assets */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Media Assets</h2>
              <p className="text-lg text-gray-600">Download high-quality assets for your coverage</p>
            </div>

            {downloadStatus && (
              <div className="max-w-2xl mx-auto mb-8 bg-teal-50 border border-teal-200 rounded-lg p-4 text-center">
                <p className="text-teal-700 font-medium">{downloadStatus}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {mediaAssets.map((asset, idx) => (
                <div key={idx} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
                  <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${asset.icon} text-3xl text-teal-500`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{asset.type}</h3>
                  <p className="text-sm text-gray-600 mb-4">{asset.description}</p>
                  <div className="text-xs text-gray-500 mb-4">{asset.size}</div>
                  <button
                    onClick={() => handleDownload(asset.type)}
                    className="w-full bg-teal-500 text-white py-2 rounded-lg font-semibold hover:bg-teal-600 transition-colors flex items-center justify-center space-x-2 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-download-line"></i>
                    <span>Download</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Press Releases */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Press Releases</h2>
              <p className="text-lg text-gray-600">Latest news and announcements</p>
            </div>

            <div className="space-y-6">
              {pressReleases.map((release, idx) => (
                <div key={idx} className="bg-gray-50 rounded-xl p-8 hover:bg-gray-100 transition-colors">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="text-sm text-teal-500 font-semibold mb-2">{release.date}</div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{release.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{release.excerpt}</p>
                    </div>
                  </div>
                  <a
                    href={release.link}
                    className="inline-flex items-center space-x-2 text-teal-500 font-semibold hover:text-teal-600 transition-colors cursor-pointer"
                  >
                    <span>Read Full Release</span>
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 bg-gradient-to-br from-teal-500 to-teal-600">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Media Inquiries</h2>
            <p className="text-xl text-teal-50 mb-8">
              For press inquiries, interviews, or additional information, please contact our media team.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <a
                href="mailto:press@teampro.com"
                className="bg-white text-teal-500 py-4 px-6 rounded-lg font-semibold hover:bg-teal-50 transition-colors flex items-center justify-center space-x-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line text-xl"></i>
                <span>press@teampro.com</span>
              </a>
              <a
                href="tel:+1-555-TEAMPRO"
                className="bg-white text-teal-500 py-4 px-6 rounded-lg font-semibold hover:bg-teal-50 transition-colors flex items-center justify-center space-x-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl"></i>
                <span>+1 (555) TEAM-PRO</span>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
